import type { CoverDoc } from 'fym-shared/src/music24/coverDoc';
import { loadKitWebFonts } from '../../music24/promote/themes/kitWebFonts';
import { COVER_EXPORT_JPEG_QUALITY, COVER_EXPORT_SIDES } from './exportRules';
import { loadCoverPicture } from './loadCoverPicture';
import { renderCoverDoc } from './renderCoverDoc';

export interface ExportedCover {
  blob: Blob;
  /** The side of the file in pixels: 3000, or a smaller side on a browser that refused it. */
  side: number;
}

function toJpeg(canvas: HTMLCanvasElement): Promise<Blob | null> {
  return new Promise((resolve) => {
    canvas.toBlob(resolve, 'image/jpeg', COVER_EXPORT_JPEG_QUALITY);
  });
}

/**
 * Draws the document with the one renderer at the export size and gives the JPEG. It tries 3000
 * px first; a browser that cannot make that canvas gets the next smaller side
 * (`COVER_EXPORT_SIDES`). It throws when no side works, or when the picture cannot be read (a
 * host with no CORS headers).
 */
export async function exportCoverJpeg(doc: CoverDoc, options: { watermark: boolean }): Promise<ExportedCover> {
  await loadKitWebFonts();
  const picture = doc.background.kind === 'picture' ? await loadCoverPicture(doc.background.url) : null;
  let lastError: unknown = new Error('The cover could not be drawn');
  for (const side of COVER_EXPORT_SIDES) {
    try {
      const canvas = document.createElement('canvas');
      canvas.width = side;
      canvas.height = side;
      const ctx = canvas.getContext('2d');
      if (ctx === null) {
        throw new Error(`No canvas of ${side} px`);
      }
      renderCoverDoc(ctx, doc, side, { picture }, { watermark: options.watermark });
      const blob = await toJpeg(canvas);
      // The canvas memory goes back at once: a phone holds only a few large canvases.
      canvas.width = 0;
      canvas.height = 0;
      if (blob !== null && blob.size > 0) {
        return { blob, side };
      }
      lastError = new Error(`The browser gave no file at ${side} px`);
    } catch (error) {
      lastError = error;
    }
  }
  throw lastError;
}
